import { useAuth } from "@client/hooks";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { WaitlistButton } from "./WaitlistButton";

export function UserHeader() {
	const { isAuthenticated, user, logout } = useAuth();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	if (!isAuthenticated || !user) {
		return null;
	}

	const handleProfileClick = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};

	const handleSignOut = () => {
		logout();
		setIsDropdownOpen(false);
	};

	return (
		<div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-md border-b border-gray-800/50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center h-14">
					{/* Logo/Title */}
					<div className="flex items-center">
						<Link
							to="/"
							className="text-lg font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent hover:from-indigo-300 hover:via-purple-300 hover:to-pink-300 transition-all duration-200"
						>
							<span className="hidden sm:inline">AI SDR Demo</span>
							<span className="sm:hidden">AI SDR Demo</span>
						</Link>
					</div>

					{/* User Info & Actions */}
					<div className="flex items-center gap-3">
						{/* Waitlist Button */}
						<WaitlistButton />

						{/* User Profile Dropdown */}
						<div className="relative" ref={dropdownRef}>
							<button
								onClick={handleProfileClick}
								className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-800/50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
							>		
								<div className="hidden sm:block text-sm text-left">
									<p className="font-medium text-gray-200 text-xs leading-tight">
										{user.name || "User"}
									</p>
									<p className="text-gray-400 text-xs leading-tight truncate max-w-32">
										{user.email}
									</p>
								</div>
								{/* Dropdown arrow */}
								<svg
									className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
										isDropdownOpen ? "rotate-180" : ""
									}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M19 9l-7 7-7-7"
									/>
								</svg>
							</button>

							{/* Dropdown Menu */}
							{isDropdownOpen && (
								<div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-1 z-10">
									{/* Mobile: Show user info */}
									<div className="sm:hidden px-4 py-2 border-b border-gray-700">
										<p className="font-medium text-gray-200 text-sm">
											{user.name || "User"}
										</p>
										<p className="text-gray-400 text-xs truncate">
											{user.email}
										</p>
									</div>
									
									{/* Sign Out Option */}
									<button
										onClick={handleSignOut}
										className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200"
									>
										Sign out
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
